import { Injectable, OnDestroy } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { NOT_TRANSLATED_SUB_PAGES } from '@core/api/constants/not-translated-pages.constants';
import { LanguageService } from '@translate/services/language.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EnApi } from '../api/enums/api.enums';
import { IMetaData, TMetaTag } from '../types/meta';

@Injectable({
  providedIn: 'root',
})
export class MetaService implements OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  private currentMeta: IMetaData;

  constructor(
    private meta: Meta,
    private languageService: LanguageService,
    private titleService: Title,
    private router: Router,
  ) {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event instanceof NavigationEnd && this.currentMeta) {
        this.updateMetaTagsForEvent(event.url);
      }
    });
  }

  public updateMetaTags(meta: IMetaData): void {
    this.currentMeta = meta;
  }

  private createMetaTags(meta: IMetaData): Map<string, string> {
    const { title, content, image: providedImage } = meta;
    const defaultImage: string = `${EnApi.BLOB_HOST}/projects/origin/main/og-min.png`;
    const image: string = providedImage ?? defaultImage;

    return new Map([
      ['description', content],
      ['og:image', image],
      ['og:description', content],
      ['og:title', title],
      ['og:image:width', '1200'],
      ['og:image:height', '600'],
      ['twitter:card', 'summary_large_image'],
      ['twitter:image', image],
      ['twitter:description', content],
      ['twitter:title', title],
      ['robots', meta?.robots || null],
    ]);
  }

  private updateMetaTagsForEvent(url: string): void {
    if (this.isNotTranslatedPage(url)) {
      this.createAndSetMetaAndTitle(this.currentMeta);
    } else {
      this.createAndSetTranslatedMeta(this.currentMeta);
    }
  }

  private isNotTranslatedPage(pageUrl: string): boolean {
    return NOT_TRANSLATED_SUB_PAGES.some((page: string) => pageUrl.includes(page));
  }

  private createAndSetMetaAndTitle(meta: IMetaData): void {
    const metaTags: Map<string, string> = this.createMetaTags(meta);

    this.setTitle(meta.title);
    this.setMetaTags(metaTags);
  }

  private getTranslate(key: string): string {
    return this.languageService.getTranslate(key);
  }

  private createAndSetTranslatedMeta(meta: IMetaData): void {
    const { title, content, image, name }: IMetaData = meta;

    const translatedMeta: IMetaData = {
      title: this.getTranslate(title),
      content: this.getTranslate(content),
      name,
      image: this.getTranslate(image),
    };

    this.createAndSetMetaAndTitle(translatedMeta);
  }

  private setMetaTags(metaTags: Map<string, string>): void {
    metaTags.forEach((value: string, key: string): void => {
      const tag: TMetaTag = key.startsWith('og:')
        ? { property: key, content: value }
        : { name: key, content: value };

      this.meta.updateTag(tag);
    });
  }

  private setTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
